import { useState, useEffect } from "react";
import { Loader2, TrendingUp, TrendingDown } from "lucide-react";
import api from "../../utils/api";
import MetricTooltip from "../../components/shared/MetricTooltip/MetricTooltip";
import { METRIC_TOOLTIPS } from "../../components/shared/MetricTooltip/metricCopy";
import MetricValue from "../../components/shared/MetricValue";
import "./RatiosTab.css";

/* Label → metricCopy key map for Cash & Working Capital metrics on this page */
const LABEL_TO_KEY = {
  "Days Sales Outstanding": "dso",
  "DEBT-TO-EQUITY RATIO": "debtToEquity",
  "CURRENT RATIO": "currentRatio",
};

/* (i) + (?) tooltip pair using a metricCopy key */
function TT({ k }) {
  const m = METRIC_TOOLTIPS[k];
  if (!m) return null;
  return (
    <>
      <MetricTooltip type="info" content={m.info} showAccountingNote={m.accountingNote} metricKey={k} testKey={k} />
      <MetricTooltip type="help" content={m.help} testKey={k} />
    </>
  );
}

/* Approved palette only - banned color families absent */
const C = {
  navy: "#1A202C",
  slate: "#64748B",
  orange: "#F5841F",
  indigo: "#6366F1",
  blue: "#3B82F6",
};

function _num(v) { const n = typeof v === "number" ? v : Number(v); return Number.isFinite(n) ? n : 0; }
function fmt(n) { return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(_num(n)); }
function fmtFull(n) { return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(_num(n)); }

/* Trend chip - orange up / indigo down */
function Trend({ value, suffix = "vs last month", positive = null }) {
  const v = _num(value);
  const isUp = positive === null ? v >= 0 : positive;
  const color = isUp ? C.orange : C.indigo;
  const Arrow = isUp ? TrendingUp : TrendingDown;
  return (
    <span style={{ color, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4 }}>
      <Arrow size={13} style={{ color }} />
      {isUp ? "+" : ""}{v}% {suffix}
    </span>
  );
}

/* Three-tier badge */
function pctBadgeClass(pct) {
  const v = _num(pct);
  if (v >= 50) return "rt-badge rt-badge-a";
  if (v >= 10) return "rt-badge rt-badge-b";
  return "rt-badge rt-badge-c";
}
function pctBadgeIconColor(pct) {
  const v = _num(pct);
  if (v >= 50) return C.orange;
  if (v >= 10) return C.blue;
  return C.slate;
}

export default function RatiosTab({ dateRange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (dateRange?.start) params.set("start_date", dateRange.start);
    if (dateRange?.end) params.set("end_date", dateRange.end);
    api.get(`/dashboard-data/ratios-detail?${params.toString()}`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [dateRange]);

  if (loading || !data) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" style={{ color: C.blue }} /></div>;

  const top = data.top_ratios || [];
  const margins = data.margin_ratios || [];
  const cf = data.cash_flow_statement || [];
  const bs = data.balance_sheet_analysis || [];
  const isa = data.income_statement_analysis || { items: [], total: { current: 0, previous: 0, amount: 0 } };

  return (
    <div className="rt-page" data-testid="ratios-tab">

      {/* ===== ROW 1: 7 tile-style cells (renders in backend order) ===== */}
      <div className="rt-card" data-testid="ratios-strip">
        <div className="rt-section-label">Ratios</div>
        <div className="rt-row-1-grid">
          {top.map((t, i) => {
            const hasMtd = !!t.has_mtd;
            const hasTrend = typeof t.change === "number" && Number.isFinite(t.change);
            return (
              <div key={i} className="rt-cell" data-testid={`ratio-${i}`}>
                <div className="rt-cell-top">
                  <span className="rt-cell-name" style={{ display: "inline-flex", alignItems: "center" }}>
                    {t.label}
                    {LABEL_TO_KEY[t.label] && <TT k={LABEL_TO_KEY[t.label]} />}
                  </span>
                  {hasMtd && <span className="rt-mtd-pill">MTD</span>}
                </div>
                <div className="rt-cell-value">{t.value}</div>
                {hasTrend && (
                  <div className="rt-cell-trend"><Trend value={t.change} suffix="" /></div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== ROW 2: 6 tile-style margin ratios ===== */}
      <div className="rt-card" data-testid="margin-ratios-strip">
        <div className="rt-row-2-grid">
          {margins.map((m, i) => (
            <div key={i} className="rt-cell" data-testid={`margin-${i}`}>
              <div className="rt-cell-label">{m.label}</div>
              <div className="rt-cell-value-md">{m.value}</div>
              <div className="rt-cell-trend-sm">
                <Trend value={Math.abs(_num(m.change))} positive={m.positive !== false && _num(m.change) >= 0} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ===== ROW 3: Left stack | Right Income Statement ===== */}
      <div className="rt-row-3">

        {/* LEFT COLUMN */}
        <div className="rt-col-left">

          {/* Statement of Cash Flows */}
          <div className="rt-card" data-testid="cash-flow-statement">
            <div className="rt-card-title-row">
              <span className="rt-card-title">Statement of cash flows</span>
              <span className="rt-mtd-outline">MTD</span>
            </div>
            {cf.map((row, i) => (
              <div key={i} className="rt-cf-row" data-testid={`cf-row-${i}`}>
                <span className="rt-cf-label">{row.label}</span>
                <span className="rt-cf-value"><MetricValue value={row.value} mode="table" charBudget={12} testid={`cf-mv-${i}`} /></span>
                <span className={pctBadgeClass(row.pct)}>
                  {row.pct}%
                  <TrendingUp size={12} style={{ color: pctBadgeIconColor(row.pct) }} />
                </span>
              </div>
            ))}
          </div>

          {/* Balance Sheet Analysis */}
          <div className="rt-card" data-testid="balance-sheet-analysis">
            <div className="rt-card-title-row">
              <span className="rt-card-title">Balance Sheet Analysis</span>
              <span className="rt-mtd-outline">MTD</span>
            </div>
            {bs.map((row, i) => (
              <div key={i} className="rt-bs-row" data-testid={`bs-row-${i}`}>
                <span className="rt-cf-label" style={{ display: "inline-flex", alignItems: "center" }}>
                  {row.label}
                  {LABEL_TO_KEY[row.label] && <TT k={LABEL_TO_KEY[row.label]} />}
                </span>
                <span className="rt-bs-value">MTD <MetricValue value={row.mtd} mode="table" charBudget={11} testid={`bs-mtd-mv-${i}`} /></span>
                <span className="rt-bs-value">{typeof row.value === "number" && row.value > 100 ? <MetricValue value={row.value} mode="table" charBudget={11} testid={`bs-val-mv-${i}`} /> : `${row.value}%`}</span>
                <span className={pctBadgeClass(row.change)}>
                  {row.change}%
                  <TrendingUp size={12} style={{ color: pctBadgeIconColor(row.change) }} />
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT COLUMN: Income Statement Analysis */}
        <div className="rt-col-right">
          <div className="rt-card" data-testid="income-statement-analysis">
            <div className="rt-card-title-row">
              <span className="rt-card-title">Income Statement Analysis</span>
              <span className="rt-mtd-outline">MTD</span>
            </div>

            {(isa.items || []).map((row, i) => (
              <div key={i} className="rt-is-row" data-testid={`is-row-${i}`}>
                <div className="rt-is-name">{row.name}</div>
                <div>
                  <span className="rt-is-sublabel">Current</span>
                  <span className="rt-is-value"><MetricValue value={row.current} mode="table" charBudget={11} testid={`is-cur-mv-${i}`} /></span>
                </div>
                <div>
                  <span className="rt-is-sublabel">Previous</span>
                  <span className="rt-is-value"><MetricValue value={row.previous} mode="table" charBudget={11} testid={`is-prev-mv-${i}`} /></span>
                </div>
                <div className="rt-is-pct-cell">
                  <span className={pctBadgeClass(row.change)}>
                    {row.change}%
                    <TrendingUp size={12} style={{ color: pctBadgeIconColor(row.change) }} />
                  </span>
                </div>
              </div>
            ))}

            <div className="rt-is-divider"></div>
            <div className="rt-is-total-row" data-testid="is-total-row">
              <div className="rt-is-total-label">Total</div>
              <div>
                <span className="rt-is-sublabel">Current</span>
                <span className="rt-is-value"><MetricValue value={isa.total?.current} mode="table" charBudget={11} testid="is-total-cur-mv" /></span>
              </div>
              <div>
                <span className="rt-is-sublabel">Previous</span>
                <span className="rt-is-value"><MetricValue value={isa.total?.previous} mode="table" charBudget={11} testid="is-total-prev-mv" /></span>
              </div>
              <div className="rt-is-total-value"><MetricValue value={isa.total?.amount} mode="table" charBudget={13} testid="is-total-amount-mv" /></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
